import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './i18n';
import { IconDots, IconLock, IconPlus, IconSearch } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { fileKind, filesInView, fmtTs, norm, searchFiles, sizeLabel, sortFiles } from './logic';
import type { FileItem, FileView } from './types';
import { KIND_CLS } from './kindCls';
import s from './filesPhone.module.css';

// Mint-Files-Phone, khung 1: tab Của tôi / Tài liệu / Media · ô tìm · chip lọc · danh sách dòng 64 · nút + (Tải lên). Chỉ đọc: nút ghi báo toast.
// Khung 2 (trình xem tệp điện thoại) và các ngăn (chia sẻ, thao tác, thêm) chưa dựng.

type Tab = 'my' | 'lib' | 'media';
const CHIPS: readonly { key: FileView; label: string }[] = [
  { key: 'mine', label: 'fl.ph.mine' },
  { key: 'shared', label: 'fl.ph.shared' },
  { key: 'large', label: 'fl.ph.large' },
  { key: 'external', label: 'fl.ph.external' },
];
const PH: Record<Tab, string> = { my: 'fl.phMy', lib: 'fl.ph.phLib', media: 'fl.ph.phMedia' };
const short = (ts: string): string => { const d = fmtTs(ts); return `${d.slice(0, 6)} ${d.slice(12)}`; };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

interface Row { id: string; n: string; sub: string; dir: boolean; kind: ReturnType<typeof fileKind>[0] | 'DIR'; locked: boolean }

const subOf = (f: FileItem, t: (k: string, v?: Record<string, string | number>) => string): string =>
  f.dir ? t('fl.ph.subDir', { n: f.cnt ?? 0, d: short(f.ts) })
  : f.owner ? t('fl.ph.subOwner', { o: f.owner, d: short(f.ts) })
  : f.sh === 'anyone' ? t('fl.ph.anyone', { s: sizeLabel(f.sz) })
  : `${sizeLabel(f.sz)} · ${short(f.ts)}${f.sh === 'org' ? t('fl.ph.org') : ''}`;

export function FilesPhone() {
  const t = useT();
  const toast = useToast();
  const mine = useList('file', { limit: 5000 });
  const shared = useList('fileShared', { limit: 5000 });
  const docsQ = useList('libDoc', { limit: 500 });
  const albumsQ = useList('fileAlbum', { limit: 500 });
  const [tab, setTab] = useState<Tab>('my');
  const [view, setView] = useState<FileView>('mine');
  const [folder, setFolder] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const noWrite = () => toast({ msg: t('fl.noWrite') });

  const all = useMemo(() => mine.data?.items ?? [], [mine.data]);
  const sh = useMemo(() => shared.data?.items ?? [], [shared.data]);
  const src = tab === 'my' ? mine : tab === 'lib' ? docsQ : albumsQ;
  const forced = readState();
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (src.isLoading) state = 'loading';
  else if (src.isError) state = src.error instanceof DataError && src.error.code === 'noperm' ? 'noperm' : 'error';

  const rows: Row[] = useMemo(() => {
    if (tab === 'my') {
      const base = filesInView(all, sh, view, view === 'mine' ? folder ?? 'root' : 'root');
      return sortFiles(searchFiles(q ? filesInView(all, sh, view) : base, q), 'ts', -1).map((f) => ({
        id: f.id, n: f.n, sub: subOf(f, t), dir: !!f.dir, kind: f.dir ? 'DIR' : fileKind(f.n)[0], locked: false,
      }));
    }
    const qn = norm(q.trim());
    if (tab === 'lib') {
      return (docsQ.data?.items ?? []).filter((d) => !d.deleted && (!qn || norm(d.n + d.title + d.type).includes(qn)))
        .map((d) => ({ id: `d${d.id}`, n: d.title, sub: `${d.type} · ${d.dom} · v${d.ver}`, dir: false, kind: fileKind(d.n)[0], locked: !!d.locked }));
    }
    return (albumsQ.data?.items ?? []).filter((a) => !qn || norm(a.n + a.loc).includes(qn))
      .map((a) => ({ id: `a${a.id}`, n: a.n, sub: t('fl.ph.albumSub', { d: fmtTs(a.ts).slice(0, 11), l: a.loc, p: a.ph }), dir: true, kind: 'IMG' as const, locked: false }));
  }, [tab, all, sh, view, folder, q, docsQ.data, albumsQ.data, t]);

  const count = (v: FileView) => filesInView(all, sh, v).length;
  const chips = tab === 'my' ? CHIPS : [];
  const showEmpty = state === 'ready' && rows.length === 0;
  const folderName = folder ? all.find((f) => f.id === folder)?.n : null;

  return (
    <Page kind="table">
      <PageHeader title={t('fl.title')} />
      <div className={s.page}>
        <Segmented
          label={t('fl.ph.store')}
          value={tab}
          onChange={(k) => { setTab(k); setQ(''); setFolder(null); }}
          items={[{ key: 'my', label: t('fl.ph.tabMy') }, { key: 'lib', label: t('fl.ph.tabLib') }, { key: 'media', label: t('fl.tabMedia') }]}
        />
        <label className={s.search}>
          <IconSearch size={17} aria-hidden="true" />
          <input aria-label={t('fl.ph.search')} placeholder={t(PH[tab])} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {chips.length > 0 && (
          <div className={s.chips}>
            {chips.map((c) => {
              const n = c.key === 'mine' ? 0 : count(c.key);
              return (
                <button key={c.key} type="button" className={s.chip} aria-pressed={view === c.key} onClick={() => { setView(c.key); setFolder(null); setQ(''); }}>
                  {t(c.label)}{n ? ` ${n}` : ''}
                </button>
              );
            })}
          </div>
        )}
        {state === 'loading' && <StateView state="loading" />}
        {state === 'error' && <StateView state="error" title={t('fl.errTitle')} />}
        {state === 'noperm' && <StateView state="noperm" icon={<IconLock size={28} aria-hidden="true" />} title={t('fl.npTitle')} desc={t('fl.ph.npDesc')} />}
        {showEmpty && <StateView state="empty" title={q ? t('fl.ph.noResult') : t('fl.ph.noFile')} desc={t('fl.ph.emptyDesc')} />}
        {state === 'ready' && rows.length > 0 && (
          <>
            {folderName && (
              <button type="button" className={s.crumb} onClick={() => setFolder(null)}>{t('fl.ph.crumb', { n: folderName })}</button>
            )}
            <ul className={s.list}>
              {rows.map((r) => (
                <li key={r.id} className={s.row}>
                  <button type="button" className={s.open} onClick={() => (r.dir && tab === 'my' ? setFolder(r.id) : noWrite())}>
                    <span className={`${s.kind} ${r.kind === 'DIR' ? s.kDir : KIND_CLS[r.kind as keyof typeof KIND_CLS]}`} aria-hidden="true">{r.kind}</span>
                    <span className={s.tx}>
                      <span className={s.nm}>
                        <span className={s.nmT}>{r.n}</span>
                        {r.locked && <IconLock size={13} role="img" aria-label={t('fl.lib.locked')} />}
                      </span>
                      <span className={s.sub}>{r.sub}</span>
                    </span>
                  </button>
                  <button type="button" className={s.more} aria-label={t('fl.actWith', { n: r.n })} onClick={noWrite}>
                    <IconDots size={18} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {state !== 'noperm' && (
        <button type="button" className={s.fab} aria-label={tab === 'media' ? t('fl.ph.newAlbum') : t('fl.upload')} onClick={noWrite}>
          <IconPlus size={28} aria-hidden="true" />
        </button>
      )}
    </Page>
  );
}
